import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteRequest, getRequest } from 'api/apiClient';
import { AppDispatch, RootState } from 'store/store';
import { List, notification } from 'antd';
import Loader from 'components/customAntd/Loader';
import { OpTabs } from 'components/customAntd/DLS/OpTabs/OpTabs';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { clearDevicesByWorkflow, resetAltaVideoForOrg, resetThumbnailPresense } from 'store/slices/altaVideoSlice';
import { useTranslation } from 'react-i18next';

interface AltaVideoConfigureProps {
    setIsAltaVideoConfigureOpen: (open: boolean) => void;
    setIsAltaVideoInstalled: (open: boolean) => void;
}

const AltaVideoConfigure: React.FC<AltaVideoConfigureProps> = ({ setIsAltaVideoConfigureOpen, setIsAltaVideoInstalled }) => {
    const dispatch: AppDispatch = useDispatch();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const [activeKey, setActiveKey] = useState<string>('connection');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const confirmModal = useConfirmModal();
    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const altaVideoData = await getRequest(`/orgs/${orgId}/integrationMsiAltaVideo`);
                setEmail(altaVideoData.data[0].msiAltaVideoUsername);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId]);

    const handleUninstall = useCallback(async () => {
        confirmModal({
            title: t('integration.confirmUninstall.title'),
            content: t('integration.confirmUninstall.message'),
            okText: t('integration.confirmUninstall.ok'),
            cancelText: t('integration.confirmUninstall.cancel'),
            onOk: async () => {
                setIsSubmitting(true);
                try {
                    await deleteRequest(`/orgs/${orgId}/integrationMsiAltaVideo`);
                    notification.success({
                        message: t('notification.success'),
                        description: t('integration.notification.altaVideoUninstallSuccess'),
                        placement: 'bottomRight',
                    });
                    // reset video presense so that it can refetch thumbnails in visitors table
                    dispatch(resetAltaVideoForOrg());
                    dispatch(clearDevicesByWorkflow());
                    dispatch(resetThumbnailPresense());

                    setIsAltaVideoConfigureOpen(false); // Go back to the main menu
                    setIsAltaVideoInstalled(false);
                } catch (error) {
                    notification.error({
                        message: t('notification.error'),
                        description: t('integration.notification.saveError'),
                        placement: 'bottomRight',
                    });
                    console.error("Form submission failed:", error);
                } finally {
                    setIsSubmitting(false);
                }
            },
        });
    }, [confirmModal, orgId, setIsAltaVideoConfigureOpen, setIsAltaVideoInstalled, dispatch, t]);

    const tabItems = [
        {
            key: 'connection',
            label: t('integration.altaVideo.configure.tab'),
            children: (
                <>
                    <List
                        header={t('integration.altaVideo.configure.header')}
                        dataSource={[
                            { label: t('integration.altaVideo.configure.connected'), email: email },
                        ]}
                        bordered
                        renderItem={item => (
                            <List.Item style={{ width: '100%' }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <span style={{ fontWeight: 'bold' }}>{item.label}:</span>
                                    <span style={{ marginLeft: '8px' }}>{item.email}</span>
                                </div>
                            </List.Item>
                        )}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <OpButton
                            type="primary"
                            danger
                            onClick={handleUninstall}
                            style={{
                                width: '120px',
                                padding: '4px 0',
                            }}
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            {t('integration.altaVideo.configure.uninstall')}
                        </OpButton>
                    </div>
                </>
            )
        }
    ];

    return (
        <>
            {(loading || isSubmitting) ? (
                <Loader />
            ) : (
                <>
                    <OpTabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
                </>
            )}
        </>
    );
};

export default AltaVideoConfigure;